import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import type { OrganizationStorageInformation } from '@connect-services/client/pages/management/data/deposit/deposit.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export interface OrganizationStorage {
    maxStorage: number;
    usedStorage: number;
}

export enum StorageStatusEnum {
    CRITICAL = 'critical',
    NORMAL = 'normal',
    WARNING = 'warning',
}

const CRITICAL_LIMIT = 90;
const WARNING_LIMIT = 70;
const BYTES_PER_GB = 1000 ** 3; // Dividing by 1000 ** 3 converts bytes to GB

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-organization-storage-usage',
    styleUrl: './organization-storage-usage.component.scss',
    templateUrl: './organization-storage-usage.component.html',
})
export class OrganizationStorageUsageComponent {
    public readonly organizationStorage = input.required<OrganizationStorageInformation>();

    protected readonly storage = computed<OrganizationStorage>(() => {
        const { usedStorageBytes, maxStorageInGo } = this.organizationStorage();

        return {
            maxStorage: maxStorageInGo,
            usedStorage: usedStorageBytes / BYTES_PER_GB,
        };
    });
    protected readonly storageMessageKey = computed<string>(() =>
        this.storageStatus() === StorageStatusEnum.CRITICAL
            ? 'storage.freeUpStorageSpace'
            : 'storage.storageAlmostFull',
    );
    protected readonly storageStatus = computed<StorageStatusEnum>(() => {
        if (this.usagePercentage() >= CRITICAL_LIMIT) {
            return StorageStatusEnum.CRITICAL;
        }

        if (this.usagePercentage() >= WARNING_LIMIT) {
            return StorageStatusEnum.WARNING;
        }

        return StorageStatusEnum.NORMAL;
    });
    protected readonly usagePercentage = computed<number>(() => {
        const { usedStorage, maxStorage } = this.storage();

        return maxStorage > 0 ? (usedStorage / maxStorage) * 100 : 0;
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly StorageStatusEnum = StorageStatusEnum;
}
